import { DisplayInfoFields } from 'src/entities/dispatch';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

/** 配列の一意化。表示項目の各値は複数 legRun にまたがるので必ずこれを通す */
function uniqStrings(values: (string | null | undefined)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => !!v && v.length > 0)));
}

/**
 * 任意項目を項目名ごとに 1 件へまとめる。値が違う案件が混ざったときは ', ' で連結する。
 * key で畳まないと同じ項目名の行がカードに何度も出る。
 */
function mergeCustomInputFields(
  legRuns: ShipmentOrderLegRunForPlanEntity[],
): { key: string; value: string }[] {
  const byKey = new Map<string, string[]>();
  legRuns.forEach((lr) => {
    (lr.custom_input_fields ?? []).forEach((field) => {
      if (!field?.key) return;
      const values = byKey.get(field.key) ?? [];
      values.push(field.value);
      byKey.set(field.key, values);
    });
  });
  return Array.from(byKey, ([key, values]) => ({ key, value: uniqStrings(values).join(', ') }));
}

/**
 * 表示項目（#316）でカードに出す値を legRun 群から組み立てる。
 * 未割当カード (toUnassignedItem) と配車カード (buildDeliveryCard) が同じ導出を通ることで、
 * 同じ案件を左右のリストで見たときに値が食い違わない。
 */
export function buildDisplayInfoFields(legRuns: ShipmentOrderLegRunForPlanEntity[]): DisplayInfoFields {
  const items = legRuns.flatMap((lr) => lr.items ?? []);
  return {
    shipperContacts: uniqStrings(legRuns.map((lr) => lr.shipper_contact_person)),
    shipperPhones: uniqStrings(legRuns.map((lr) => lr.shipper_phone_number)),
    shipperEmails: uniqStrings(legRuns.map((lr) => lr.shipper_email_address)),
    products: uniqStrings(items.map((item) => item.name)),
    productCodes: uniqStrings(items.map((item) => item.product_code)),
    requiredVehicleTypes: uniqStrings(legRuns.flatMap((lr) => lr.designated_truck_klasses ?? [])),
    requiredTruckTypes: uniqStrings(legRuns.flatMap((lr) => lr.designated_truck_car_models ?? [])),
    bedHeights: uniqStrings(legRuns.flatMap((lr) => lr.designated_truck_loading_platform_heights ?? [])),
    bedWidths: uniqStrings(legRuns.flatMap((lr) => lr.designated_truck_loading_platform_widths ?? [])),
    bedLengths: uniqStrings(legRuns.flatMap((lr) => lr.designated_truck_loading_platform_lengths ?? [])),
    floorSpecs: uniqStrings(legRuns.flatMap((lr) => lr.designated_truck_floor_specifications ?? [])),
    equipments: uniqStrings(legRuns.flatMap((lr) => lr.designated_truck_features ?? [])),
    designatedTrucks: uniqStrings(legRuns.flatMap((lr) => (lr.allowed_trucks ?? []).map((t) => t.name))),
    ngDrivers: uniqStrings(legRuns.flatMap((lr) => (lr.denied_drivers ?? []).map((d) => d.name))),
    customFields: mergeCustomInputFields(legRuns),
    invoiceNo: uniqStrings(legRuns.map((lr) => lr.code)).join(', '),
  };
}
